import { Button, Tooltip } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import ForumIcon from "@material-ui/icons/Forum";
import PersonIcon from "@material-ui/icons/Person";
import ReplyIcon from "@material-ui/icons/Reply";
import ScheduleIcon from "@material-ui/icons/Schedule";
import React, { useEffect, useState } from "react";
import InputEmoji from "react-input-emoji";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import {
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { SERVER_URL } from "../../../../api";
import {
	deleteReply,
	getRepliesByThread,
	postReply,
} from "../../../../api/reply";
import { getThreadById } from "../../../../api/thread";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import EditReplyDialog from "../../../components/dialogs/EditReplyDialog";
import { dateFormatter } from "../../../components/tables/table";
import { useSkeleton } from "../../../hooks/useSkeleton";

function getReplies(parentId, data) {
	if (!parentId || !data?.length) return [];
	const replies = data.filter((r) => r.parent === parentId) || [];
	return replies;
}

function RepliesList({
	replies,
	parentReply,
	setEditReply,
	setParentReply,
	setDeleteReplyId,
	setOpenConfirmDialog,
	setOpenEditReplyDialog,
	newMessageReply,
	setNewMessageReply,
	handleSend,
	childReplies,
	newReply,
}) {
	if (!replies) return <></>;
	return replies?.map((reply) => (
		<div key={reply._id} className="reply-stack">
			<>
				<div
					key={reply._id}
					id={reply._id}
					className={
						"reply mb-5 " +
						(newReply === reply._id ? "new-reply" : "")
					}
				>
					<div className="reply-img-container">
						<img
							className="reply-img"
							alt={reply.sender?.fullName}
							src={
								reply?.sender?.imagesURLs?.length > 0
									? `${SERVER_URL}/${reply?.sender?.imagesURLs[0]}`
									: reply.sender?.role?.includes("admin")
									? toAbsoluteUrl(
											"/media/admin-profile-icon.png"
									  )
									: toAbsoluteUrl("/media/profile-icon.png")
							}
							width={50}
						/>
					</div>
					<div className="reply-content">
						<h5>
							{reply.sender?.fullName}
							<span
								className="mx-3"
								style={{ fontSize: "small", color: "gray" }}
							>
								{dateFormatter(reply?.createdAt)}
							</span>
							<span
								className="reply-button pointer"
								onClick={() => {
									setParentReply(reply._id);
									setNewMessageReply("");
								}}
							>
								<ReplyIcon /> Reply
							</span>
							<Tooltip title="Edit">
								<span
									className="edit-reply-button pointer"
									onClick={() => {
										let editReply = {
											...reply,
											sender: reply?.sender?._id,
										};
										setEditReply(editReply);
										setOpenEditReplyDialog(true);
									}}
								>
									<Edit />
								</span>
							</Tooltip>
							<Tooltip title="Delete">
								<span
									className="delete-reply-button pointer"
									onClick={() => {
										setDeleteReplyId(reply._id);
										setOpenConfirmDialog(true);
									}}
								>
									<Delete />
								</span>
							</Tooltip>
						</h5>
						<p dangerouslySetInnerHTML={{ __html: reply.text }}></p>
					</div>
				</div>
				<div className="nested-replies-stack">
					<div className="nested-replies">
						<RepliesList
							replies={getReplies(reply._id, childReplies)}
							parentReply={parentReply}
							setEditReply={setEditReply}
							setDeleteReplyId={setDeleteReplyId}
							setOpenConfirmDialog={setOpenConfirmDialog}
							setOpenEditReplyDialog={setOpenEditReplyDialog}
							newMessageReply={newMessageReply}
							setNewMessageReply={setNewMessageReply}
							handleSend={handleSend}
							childReplies={childReplies}
							newReply={newReply}
						/>
					</div>
				</div>
				{parentReply === reply._id && (
					<>
						<div className="reply-sender">
							<InputEmoji
								shouldReturn={true}
								value={newMessageReply}
								onChange={(message) =>
									setNewMessageReply(message)
								}
							/>
							<button
								type="button"
								className="btn btn-primary"
								onClick={handleSend(true)}
							>
								Send
							</button>
						</div>
					</>
				)}
			</>
		</div>
	));
}

export default function ViewCourseThreadsPage() {
	const [thread, setThread] = useState(null);
	const [deleteReplyId, setDeleteReplyId] = useState(null);
	const [editReply, setEditReply] = useState(null);

	const [rootReplies, setRootReplies] = useState([]);
	const [childReplies, setChildReplies] = useState([]);
	const [parentReply, setParentReply] = useState(null);
	const [newReply, setNewReply] = useState(null);
	const [newMessage, setNewMessage] = useState("");
	const [newMessageReply, setNewMessageReply] = useState("");
	const [showReplies, setShowReplies] = useState(true);
	const [numReplies, setNumReplies] = useState(0);
	const user = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(null);
	const [openEditReplyDialog, setOpenEditReplyDialog] = useState(null);

	const courseId = useParams().id;
	const threadId = useParams().threadId;
	const history = useHistory();
	const [refresh, setRefresh] = useState(true);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!threadId) {
			disableLoadingData();
			alertError({ error: null, customMessage: "Could not get thread." });
			history.push("/sports/courses/" + courseId + "/threads/");
			return;
		}
		getThreadById(threadId)
			.then((res) => {
				if (res.status === 200) {
					setThread(res.data);
					setNumReplies(res.data.replies);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get thread.",
				});
				history.push("/sports/courses/" + courseId + "/threads/");
			});
		getRepliesByThread(threadId)
			.then((res) => {
				if (res.status === 200) {
					setRootReplies(
						res.data.filter(
							(item) => !item.parent || item.parent === null
						)
					);
					setChildReplies(
						res.data.filter((item) => item.parent !== null)
					);
					setRefresh(false);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get thread replies.",
				});
				history.push("/sports/courses/" + courseId + "/threads/");
			});
	}, [threadId, disableLoadingData, history, refresh]);

	const handleSend = (replying) => async (e) => {
		e.preventDefault();
		let reply = {
			sender: user._id,
			text: newMessage,
			thread: thread._id,
		};
		if (replying) {
			if (parentReply)
				reply = {
					...reply,
					parent: parentReply,
					text: newMessageReply,
				};
			else
				alertError({
					error: null,
					customMessage: "Could not send reply.",
				});
		}
		try {
			const { data } = await postReply(reply);
			if (replying) {
				setNewMessageReply("");
				setChildReplies(childReplies.concat({ ...data, sender: user }));
			} else {
				setNewMessage("");
				setRootReplies(rootReplies.concat({ ...data, sender: user }));
			}
			setNumReplies(numReplies + 1);
			setParentReply(null);
			setNewReply(data._id);
			setTimeout(() => {
				setNewReply(null);
			}, 2000);
		} catch (err) {
			alertError({ error: err, customMessage: "Could not send reply." });
		}
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title={thread?.title || "----"}>
						<CardHeaderToolbar>
							<span
								className="badge badge-light mx-2"
								style={{ fontSize: "small" }}
							>
								<PersonIcon /> {thread?.createdBy?.fullName}
							</span>
							<span
								className="badge badge-light"
								style={{ fontSize: "small" }}
							>
								<ScheduleIcon />{" "}
								{dateFormatter(thread?.createdAt)}
							</span>
						</CardHeaderToolbar>
					</CardHeader>
					<CardBody>
						<div
							dangerouslySetInnerHTML={{ __html: thread?.text }}
						/>
						<br />
						<br />
						<Tooltip
							title={
								showReplies ? "Hide replies" : "Show replies"
							}
						>
							<span
								className={`badge badge-${
									showReplies ? "primary" : "dark"
								} pointer`}
								style={{ fontSize: "small" }}
								onClick={() => setShowReplies(!showReplies)}
							>
								<ForumIcon /> {numReplies} replies
							</span>
						</Tooltip>

						<br />
						<br />
						{showReplies && (
							<RepliesList
								replies={rootReplies}
								parentReply={parentReply}
								setParentReply={setParentReply}
								setEditReply={setEditReply}
								setDeleteReplyId={setDeleteReplyId}
								setOpenConfirmDialog={setOpenConfirmDialog}
								setOpenEditReplyDialog={setOpenEditReplyDialog}
								newMessageReply={newMessageReply}
								setNewMessageReply={setNewMessageReply}
								handleSend={handleSend}
								childReplies={childReplies}
								newReply={newReply}
							/>
						)}
					</CardBody>
					<CardFooter>
						<div className="reply-sender">
							<InputEmoji
								shouldReturn={true}
								value={newMessage}
								onChange={(message) => setNewMessage(message)}
							/>
							<button
								type="button"
								className="btn btn-primary"
								onClick={handleSend(false)}
							>
								Send
							</button>
						</div>
					</CardFooter>
				</Card>
				<ConfirmDialog
					title={
						"¿Estás seguro de que quieres eliminar este mensaje?"
					}
					children={"Esto también eliminará todas sus respuestas"}
					open={openConfirmDialog}
					setOpen={setOpenConfirmDialog}
					onConfirm={() => {
						deleteReply(deleteReplyId)
							.then((res) => {
								if (res.status === 204 || res.status === 200) {
									alertSuccess({
										title: "Deleted!",
										customMessage:
											"Reply removed successfully.",
									});
									setRefresh(true);
								}
							})
							.catch((error) => {
								alertError({
									error: error,
									customMessage: "Could not remove reply.",
								});
							});
					}}
				/>
				<EditReplyDialog
					title="Edit reply"
					open={openEditReplyDialog}
					setOpen={setOpenEditReplyDialog}
					data={editReply}
					onSave={() => setRefresh(true)}
				/>
				<div style={{ display: "flex", flexDirection: "row" }}>
					<Button
						onClick={() =>
							history.push(
								"/sports/courses/" + courseId + "/threads/"
							)
						}
						variant="outlined"
						style={{ marginRight: "20px" }}
					>
						Back
					</Button>
				</div>
			</>
		);
}
